import Box from '@mui/material/Box';
import {
	AutocompleteGroupedOption,
	UseAutocompleteRenderedOption
} from '@mui/material/useAutocomplete';
import { HTMLAttributes, Key, useMemo } from 'react';

import {
	isAdvancedSearch,
	isQuickSearchResult,
	QuickSearchOption
} from '../_types/QuickSearch.types';
import { AdvancedSearchOption } from '../AdvancedSearchOption/AdvancedSearchOption';
import { ProfileCard } from '../ProfileCard/ProfileCard';

import { NoQuickSearchResults } from './NoQuickSearchResults/NoQuickSearchResults';
import { quickSearchResultsStyles as sx } from './QuickSearchResults.styles';

type Props = {
	query: string;
	groupedOptions: AutocompleteGroupedOption<QuickSearchOption>[];
	getOptionProps: (
		renderedOption: UseAutocompleteRenderedOption<QuickSearchOption>
	) => HTMLAttributes<HTMLLIElement> & {
		key: Key;
	};
	anchorEl: HTMLElement | null;
};

export const QuickSearchResults = ({
	query,
	groupedOptions,
	getOptionProps,
	anchorEl
}: Props) => {
	const searchCount = useMemo(
		() =>
			groupedOptions.reduce((totalCount, group) => {
				const groupCount = group.options.filter(o =>
					isQuickSearchResult(o)
				).length;

				return totalCount + groupCount;
			}, 0),
		[groupedOptions]
	);

	if (searchCount === 0) {
		return (
			<NoQuickSearchResults
				query={query}
				getOptionProps={getOptionProps}
				groupedOptions={groupedOptions}
				anchorEl={anchorEl}
			/>
		);
	}

	return (
		<Box component='ul' sx={sx.listGroup}>
			{groupedOptions.map(group =>
				group.options.map((option, index) => {
					const { key, ...optionProps } = getOptionProps({
						option,
						index: group.index + index
					});

					if (!isQuickSearchResult(option) && !isAdvancedSearch(option))
						return null;

					return (
						<Box key={key} component='li' {...optionProps} sx={sx.listOption}>
							{isQuickSearchResult(option) && (
								<ProfileCard
									profile={option.entity}
									view='compact'
									wrapChips={false}
								/>
							)}
							{isAdvancedSearch(option) && <AdvancedSearchOption />}
						</Box>
					);
				})
			)}
		</Box>
	);
};
